import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, FileDown, Sparkles, QrCode, Award } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { EDI } from "@/constants/testIds";

// Encode QR payload as a Google Charts image (no extra dependency).
function qrImageSrc(payload) {
    const enc = encodeURIComponent(payload || "");
    return `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=0&data=${enc}`;
}

export default function Certification() {
    const [cert, setCert] = useState(null);
    const [busy, setBusy] = useState(false);
    const [downloading, setDownloading] = useState(null);

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const load = async () => {
        setBusy(true);
        try { const { data } = await api.get("/edi/certification"); setCert(data); }
        catch { toast.error("Certification generation failed"); }
        finally { setBusy(false); }
    };

    const download = async (fmt) => {
        if (!cert) return;
        setDownloading(fmt);
        try {
            const token = localStorage.getItem("sgsb.token");
            const resp = await fetch(`${API_BASE}/reports/export`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    title: `SGS Bateman · Executive Production Readiness Certification · ${cert.certificate_id}`,
                    content: cert.report_markdown,
                    fmt,
                    confidentiality: "Board",
                }),
            });
            if (!resp.ok) throw new Error(String(resp.status));
            const cd = resp.headers.get("Content-Disposition") || "";
            const match = cd.match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : `SGSBateman_Certification.${fmt}`;
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 3000);
            toast.success(`Certification · ${fmt.toUpperCase()} downloaded`);
        } catch { toast.error(`Certification · ${fmt.toUpperCase()} export failed`); }
        finally { setDownloading(null); }
    };

    return (
        <div className="space-y-8" data-testid={EDI.certificationRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        Executive Production Readiness Certification™
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-2xl font-body text-sm">
                        A digitally-signed executive certificate confirming that every capability
                        pillar has been verified for board-level demonstration.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <Award className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">Signed · board-ready</span>
                </div>
            </div>

            {busy && !cert && <div className="kicker text-slate-500">Composing certification…</div>}

            {cert && (
                <>
                    {/* Certificate hero */}
                    <div className="glass rounded-2xl relative overflow-hidden">
                        <div className="absolute inset-0 opacity-40 pointer-events-none"
                             style={{ background: "radial-gradient(ellipse at 90% 10%, rgba(212,175,55,0.28), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(59,130,246,0.20), transparent 55%)" }} />
                        <div className="relative p-8 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6">
                            <div>
                                <div className="kicker text-[var(--sgsb-gold)]">Certificate</div>
                                <div className="font-display font-black text-white text-2xl md:text-3xl tracking-tighter mt-2">
                                    {cert.certificate_id}
                                </div>
                                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {[
                                        ["Overall Readiness", `${cert.overall_readiness_pct}%`],
                                        ["Issued",            cert.issued_at.replace("T", " ").replace("Z", " UTC")],
                                        ["SharePoint",        cert.sharepoint_mode.toUpperCase()],
                                        ["Confidentiality",   "Board"],
                                    ].map(([k, v]) => (
                                        <div key={k} className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                                            <div className="kicker text-slate-500 text-[10px]">{k}</div>
                                            <div className="mt-1 font-display font-black text-white text-lg tracking-tight">{v}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-4 text-xs text-slate-400 font-body">
                                    Digital signature — <code data-testid={EDI.certificationSignature} className="text-[var(--sgsb-gold)]">{cert.signature}</code>
                                </div>
                            </div>
                            <div className="flex flex-col items-center gap-3">
                                <div className="rounded-md bg-white p-3">
                                    <img
                                        alt="Certificate QR"
                                        data-testid={EDI.certificationQr}
                                        src={qrImageSrc(cert.qr_payload)}
                                        width={180} height={180}
                                    />
                                </div>
                                <div className="text-[11px] text-slate-500 kicker text-center max-w-[180px]">
                                    Scan to verify certificate
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Pillars */}
                    <div className="glass rounded-xl overflow-hidden">
                        <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                            <ShieldCheck className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                            <h3 className="font-display font-black text-white tracking-tight">Verified capability pillars</h3>
                            <div className="flex-1" />
                            <span className="kicker text-[10px] text-slate-500">{cert.pillars.length} pillars</span>
                        </div>
                        <div className="divide-y divide-slate-800/60">
                            {cert.pillars.map((p) => (
                                <div key={p.pillar} className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                        <div className="font-display font-black text-white text-sm tracking-tight flex-1">{p.pillar}</div>
                                        <div className="w-52 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                                            <div className="h-full bg-[var(--sgsb-gold)]" style={{ width: `${p.score}%` }} />
                                        </div>
                                        <span className="text-[var(--sgsb-gold)] font-display font-bold text-sm w-12 text-right">{p.score}%</span>
                                    </div>
                                    <div className="mt-1 text-xs text-slate-400 font-body">{p.note}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Downloads */}
                    <div className="glass rounded-xl p-6 flex flex-wrap items-center gap-3">
                        <Sparkles className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        <span className="kicker text-[10px] text-slate-400">Download signed certificate</span>
                        <div className="flex-1" />
                        {[["pdf","PDF"],["docx","Word"],["pptx","PowerPoint"]].map(([fmt, label]) => (
                            <button key={fmt}
                                data-testid={EDI.certificationExport(fmt)}
                                onClick={() => download(fmt)} disabled={downloading}
                                className="inline-flex items-center gap-2 rounded-md border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)] px-4 py-2 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold)]/10 disabled:opacity-40 transition-colors duration-200"
                            >
                                <FileDown className="h-3.5 w-3.5" strokeWidth={2} />
                                {downloading === fmt ? "Preparing…" : label}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
